import { Breakpoints, TextTag } from '@volkswagen-onehub/components-core';
import {
  TextAppearance,
  TextLineHeight,
  TextSize,
  TextWeight,
  Spinner,
} from '@vw-marketing/us-components';
import React from 'react';
import { Box, TextBox } from '../../../Box';
import { FontWeight } from '../../../../utils/typography';
import { SearchResultItem } from './SearchResultItem';
import { useStore } from '../../../../store/store';
import { resetQsgSearch } from '../../../../store/slices/qsg/actions';
import { AutoCompleteInput } from '../../../Input';
import { Option } from '../../../Input/types';
import { useQueryParamValue } from '../../../../hooks/custom/use-query-param-value';
import { useTrackingManager } from '../../../../hooks/use-tracking-manager';
import { normalizeSectionId } from '../../../../utils/query-param-helpers';
import { useSearchAutocomplete } from '../../../../hooks/custom/use-search-autocomplete';

interface SearchContentProps {
  goToSectionFromSearch: (sectionId: string, searchTerm: string, result: any, resultRelativeIndex: any, totalResults: any) => void;
  onSearch: (option: Option | null, isAutocompleteClick?: boolean) => void;
  isDesktop: boolean;
  isMobile: boolean;
  onSelectSearchResult?: () => void;
}

export const SearchContent = ({
  goToSectionFromSearch,
  onSearch,
  isDesktop,
  isMobile,
  onSelectSearchResult,
}: SearchContentProps) => {
  const [state, dispatch] = useStore();
  const trackingManager = useTrackingManager();
  const {
    qsgNavigation,
    qsgSearchTerm,
    qsgSearchPage,
    qsgSearchResultsMap,
    qsgSearchLoading,
    qsgSearchErrors,
    qsgContent,
  } = state.qsg;

  const modelYear = qsgNavigation?.modelYear ?? '-';
  const modelName = qsgNavigation?.modelName ?? '-';

  const model = qsgNavigation
    ? {
        modelId: qsgNavigation.modelId,
        modelName: qsgNavigation.modelName,
        modelYear: qsgNavigation.modelYear,
      }
    : undefined;

  const urlSearchTerm = useQueryParamValue('search', '');

  const { autocompleteOptions, handleSearchChange, qsgSearchOption } =
    useSearchAutocomplete({
      qsgContent,
      qsgSearchTerm,
      urlSearchTerm,
      limit: 10,
    });

  // Get search results for current term and page
  const searchKey = qsgSearchTerm ? `${qsgSearchTerm}:${qsgSearchPage}` : '';
  const searchResults = searchKey ? qsgSearchResultsMap[searchKey] : null;

  // Ensure results match the current page (prevent showing stale results)
  const isValidResults = searchResults && searchResults.page === qsgSearchPage;
  const displayResults = isValidResults ? searchResults : null;

  const hasAnySearchResults = qsgSearchTerm
    ? Object.keys(qsgSearchResultsMap).some((key) =>
        key.startsWith(`${qsgSearchTerm}:`)
      )
    : false;

  // Find the first loaded result for this search term (any page) to use for title/pagination
  const firstLoadedResult = qsgSearchTerm
    ? Object.entries(qsgSearchResultsMap).find(([key]) =>
        key.startsWith(`${qsgSearchTerm}:`)
      )?.[1]
    : null;

  const isSearchLoading = qsgSearchTerm && !hasAnySearchResults;

  const isSearchPageLoading = searchKey
    ? qsgSearchLoading[searchKey] ||
      (!displayResults && !qsgSearchErrors[searchKey])
    : false;

  const searchError = searchKey ? qsgSearchErrors[searchKey] : null;

  const updateSection = (sectionId: string, searchTerm: string, result: any, resultRelativeIndex: any, totalResults: any) => {
    // Mark that this section change came specifically from clicking a search result
    onSelectSearchResult?.();
    dispatch(resetQsgSearch());
    goToSectionFromSearch(normalizeSectionId(sectionId), searchTerm, result, resultRelativeIndex, totalResults);
  };

  if (isSearchLoading) {
    return (
      <Box
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        <Spinner />
      </Box>
    );
  }

  if (searchError)
    return (
      <Box>
        <TextBox>Error loading search results: {searchError}</TextBox>
      </Box>
    );

  if (!firstLoadedResult) return null;

  return (
    <Box>
      {isDesktop ? (
        <TextBox
          appearance={TextAppearance.headline300}
          tag={TextTag.h1}
          style={{
            [Breakpoints.default]: {
              fontSize: TextSize.TextSize32,
              fontWeight: TextWeight.bold,
              lineHeight: TextLineHeight.TextLineHeight36,
            },
            [Breakpoints.b1280]: {
              fontSize: TextSize.TextSize48,
              fontWeight: TextWeight.bold,
              lineHeight: TextLineHeight.TextLineHeight52,
            },
          }}
          customStyle={(theme) => ({
            color: theme.colors.content.active,
          })}
        >
          <TextBox
            appearance={TextAppearance.headline300}
            tag={TextTag.span}
            customStyle={{
              fontWeight: FontWeight.light,
            }}
          >
            {firstLoadedResult.totalCount} results for
            <br />
          </TextBox>
          "{qsgSearchTerm}"
        </TextBox>
      ) : (
        <Box>
          <TextBox
            appearance={TextAppearance.copy200}
            tag={TextTag.p}
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize14,
                fontWeight: TextWeight.regular,
                lineHeight: TextLineHeight.TextLineHeight24,
              },
              [Breakpoints.b1280]: {
                fontSize: TextSize.TextSize16,
                fontWeight: TextWeight.regular,
                lineHeight: TextLineHeight.TextLineHeight28,
              },
            }}
            wrapperStyle={{
              margin: 0,
            }}
            customStyle={(theme) => ({
              color: theme.colors.content.tertiary,
            })}
          >
            Quick Start Guide - {modelYear} {modelName}
          </TextBox>
          <TextBox
            appearance={TextAppearance.headline300}
            tag={TextTag.h1}
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize24,
                fontWeight: TextWeight.bold,
                lineHeight: TextLineHeight.TextLineHeight28,
              },
              [Breakpoints.b1280]: {
                fontSize: TextSize.TextSize28,
                fontWeight: TextWeight.bold,
                lineHeight: TextLineHeight.TextLineHeight30,
              },
            }}
            wrapperStyle={{
              margin: 0,
            }}
          >
            Search Results
          </TextBox>
          <Box style={{ margin: '16px 0' }}>
            <AutoCompleteInput
              inputId='searchField2'
              label='Search'
              hideLabel={true}
              required={true}
              defaultValue={urlSearchTerm || undefined}
              value={qsgSearchOption}
              options={autocompleteOptions}
              onDebounceChange={handleSearchChange}
              onSelect={(opt, isAutocompleteClick) =>
                onSearch(opt, isAutocompleteClick)
              }
              placeholder='What do you need help with?'
            />
          </Box>
          <TextBox
            appearance={TextAppearance.copy200}
            tag={TextTag.p}
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize16,
                fontWeight: TextWeight.bold,
                lineHeight: TextLineHeight.TextLineHeight20,
              },
              [Breakpoints.b1280]: {
                fontSize: TextSize.TextSize18,
                fontWeight: TextWeight.bold,
                lineHeight: TextLineHeight.TextLineHeight22,
              },
            }}
            wrapperStyle={{
              margin: 0,
            }}
          >
            {firstLoadedResult.totalCount} Results for "{qsgSearchTerm}"
          </TextBox>
        </Box>
      )}
      <Box>
        {isSearchPageLoading ? (
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '650px',
              width: '100%',
            }}
          >
            <Spinner />
          </Box>
        ) : (
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              marginTop: isDesktop ? '71px' : '32px',
            }}
          >
            {(displayResults?.results ?? []).map((result, idx) => {
              const totalResults = displayResults?.totalCount ?? 0;
              const limit = displayResults?.limit ?? 10;
              // Calculate relative index: (page - 1) * limit + idx + 1 (1-indexed)
              const resultRelativeIndex = (qsgSearchPage - 1) * limit + idx + 1;

              return (
                <SearchResultItem
                  key={`search-result-${idx}`}
                  result={result}
                  onSelect={updateSection}
                  isMobile={isMobile}
                  searchTerm={qsgSearchTerm}
                  trackingManager={trackingManager}
                  resultRelativeIndex={resultRelativeIndex}
                  totalResults={totalResults}
                  model={model}
                />
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};
